import { z } from 'zod';
export const COPY = {
    /* ── shell ────────────────────────────────────────────────────────────── */
    'shell.skipLink': {
        default: 'Skip to content',
        note: 'The first focusable element on every page. Keyboard users hear this first.',
    },
    'shell.brand': {
        default: 'JC Audience Orchestra',
        note: 'The brand, in the masthead. Set in display type, uppercased by CSS.',
    },
    'shell.yearLabel': {
        default: '{{year}} auditions',
        note: 'Beside the brand in the masthead.',
        vars: ['year'],
    },
    'shell.navLabel': {
        default: 'Sections',
        note: 'Accessible name for the main navigation. Read by screen readers only.',
    },
    'shell.footerOrg': {
        default: 'JC Audience Orchestra',
        note: 'The organisation name in the footer, on every page.',
    },
    'shell.privacyNotice': {
        default: 'Privacy notice',
        note: 'Footer link to the privacy notice. Required reading before anyone applies.',
    },
    'shell.terms': { default: 'Terms and conditions', note: 'The footer link to the audition terms.' },
    'shell.help': { default: 'Help', note: 'The footer link to the help page.' },
    'shell.privacyUrl': {
        default: 'https://terms.jacobcollier.com/privacy',
        note: 'Where the privacy notice lives. It is the orchestra\u2019s own page rather '
            + 'than a page of this app, so it is a setting here: moving it is an edit, '
            + 'not a deploy.',
    },
    'shell.signOut': {
        default: 'Sign out',
        note: 'The masthead button that ends the session. Shown only when somebody is signed in.',
    },
    'shell.capabilitiesError': {
        default: 'We could not check what your account can open.',
        note: 'Shown when the entitlements check fails. The app fails closed, so nothing is listed.',
    },
    /* ── navigation ───────────────────────────────────────────────────────── */
    'nav.myApplications': { default: 'My applications', note: 'Masthead link to an applicant own applications and offers.' },
    'nav.judging': { default: 'Judging', note: 'Masthead link to the adjudicator dashboard. Shown only to adjudicators.' },
    'nav.review': { default: 'Review', note: 'Masthead link to the ensemble review. Shown only to the conductor.' },
    'nav.masterSheet': { default: 'Master sheet', note: 'Masthead link to the master sheet. Shown only to the orchestra team.' },
    'nav.setup': { default: 'Setup', note: 'Masthead link to audition setup. Shown only to the orchestra team.' },
    'nav.adjudicators': { default: 'Adjudicators', note: 'Masthead link to adjudicator invitations. Shown only to the orchestra team.' },
    'nav.wording': { default: 'Wording', note: 'Masthead link to interface copy and message templates. Shown only to the orchestra team.' },
    'nav.principals': { default: 'Principals', note: 'Masthead link to the principal proposals. Shown only to the orchestra team.' },
    'nav.release': { default: 'Outcomes', note: 'Masthead link to the release page. Shown only to the orchestra team.' },
    /* ── home ─────────────────────────────────────────────────────────────── */
    /*
     * The landing page, which is Fran's copy and only Fran's copy.
     *
     * Everything this page used to say about how the orchestra reaches its
     * answer — the steps, what to have ready, the schedule, a video of last
     * year — was written here and has been taken out. What an applicant is told
     * before they apply is the orchestra's to say, not this app's to invent, and
     * two versions of it is one more than anybody can keep true.
     */
    'home.eyebrow': { default: 'Applications', note: 'The small uppercase label above the home page title.' },
    'home.title': {
        /*
         * A straight apostrophe, on purpose, and the only one in this file.
         *
         * This line is set in the display face, and Woodland Bold has 121 glyphs
         * with no curly apostrophe among them — so a \u2019 here drops that one
         * character into the body face, mid-word, in 32px capitals. Every other
         * string in this catalogue is body text and keeps its proper apostrophe.
         */
        default: 'Submit your application to join Jacob\'s Audience Symphony Orchestra',
        note: 'The one display-type line on the home page. It uses a straight '
            + 'apostrophe because the display face has no curly one.',
    },
    'home.lede': {
        default: 'After the success of last year\u2019s concert in San Francisco, Jacob '
            + 'Collier\u2019s Audience Symphony Orchestra returns for one night only at '
            + 'San Diego\u2019s Rady Shell on March 6th 2027, and we need you.',
        note: 'The opening paragraph on the home page.',
    },
    'home.seeking': {
        default: 'We are seeking orchestral musicians with a high level of artistry and '
            + 'orchestral experience to create this one-night-only orchestra. Whether '
            + 'you have studied at a conservatoire or leading music institution, are '
            + 'working as a professional, or are an accomplished amateur with '
            + 'high-level training (or equivalent), we would love to hear from you. '
            + 'The orchestra is for players aged 18 and over.',
        note: 'The second paragraph on the home page, about who the orchestra is looking for.',
        guard: {
            mustContain: '18',
            because: 'The form blocks under-18s and keeps no record of the attempt. Saying so '
                + 'before somebody starts saves them filling in a form that will be refused.',
        },
    },
    /* ── the timeline ─────────────────────────────────────────────────────── */
    'home.process.heading': { default: 'Timeline', note: 'Heading over the three dates.' },
    'home.step.apply.when': {
        default: 'October 14th, 12:01am PT',
        note: 'When applications open. Set on the Wording page if the date moves.',
    },
    'home.step.apply.title': { default: 'Audition applications open', note: 'The first dated line.' },
    'home.step.results.when': {
        default: 'November 1st, 11:59pm PT',
        note: 'When applications close. Set on the Wording page if the date moves.',
    },
    'home.step.results.title': { default: 'Audition applications close', note: 'The second dated line.' },
    'home.step.accept.when': {
        default: 'November 9th, 11:59pm PT',
        note: 'When successful applicants are told. Set on the Wording page if the date moves.',
    },
    'home.step.accept.title': {
        default: 'Successful applicants will be notified',
        note: 'The third dated line.',
    },
    'home.process.note': {
        default: 'If you do not hear from us by that date, it means you were not selected. '
            + 'However, if you opted into the reserve list at the end of the application, '
            + 'please keep an eye on your inbox, as a place may still become available '
            + 'and we will be reaching out on a case by case basis.',
        note: 'Under the timeline. Says plainly that silence is an answer, which is the '
            + 'one thing an applicant cannot work out for themselves.',
    },
    /* ── eligibility ──────────────────────────────────────────────────────── */
    'home.before.heading': { default: 'Am I eligible to apply?', note: 'Heading over the three conditions.' },
    'home.before.age': {
        default: 'At least 18 years of age at the time you submit your audition videos.',
        note: 'First condition. The form refuses an under-18 date of birth and keeps no record of the attempt.',
        guard: {
            mustContain: '18',
            because: 'The form blocks under-18s. Saying so before somebody starts saves them '
                + 'filling in a form that will be refused.',
        },
    },
    'home.before.residency': {
        default: 'A current legal resident of the fifty United States or D.C., with the '
            + 'unrestricted right to work in the U.S. or employment authorisation in the U.S.',
        note: 'Second condition. Comes from the Terms and Conditions, which govern.',
    },
    'home.before.attend': {
        default: 'Able to attend rehearsals in San Diego on March 5th, 10am\u20131pm and 2\u20135pm, '
            + 'and rehearsals plus the show on March 6th (timings to be discussed).',
        note: 'Third condition. The dates the orchestra needs somebody to be free.',
    },
    /* ── what a place comes with ──────────────────────────────────────────── */
    'home.receive.heading': {
        default: 'Selected participants will receive',
        note: 'Heading over what somebody gets if they are offered a place.',
    },
    'home.receive.fee': {
        default: 'A performance fee, with more details provided in the Terms and Conditions.',
        note: 'First of the two things a selected player receives.',
    },
    'home.receive.tickets': {
        default: 'Two complimentary tickets to the show for your guests.',
        note: 'Second of the three things a selected player receives.',
    },
    'home.receive.futureTickets': {
        default: 'Two complimentary tickets for a future headline Jacob show of your '
            + 'choice (details in Terms and Conditions)',
        note: 'Third of the three things a selected player receives. Section 4 of the '
            + 'Terms says the concert and the seats are "determined by Producer in '
            + 'its sole discretion", and excludes festivals, private performances '
            + 'and VIP or premium inventory — so "of your choice" is wider than what '
            + 'the Terms commit to. Flagged with Ben on 2026-10-14; his wording.',
    },
    /* ── how to apply ─────────────────────────────────────────────────────── */
    'home.apply.heading': { default: 'How do I apply?', note: 'Heading over what the form asks for.' },
    /*
     * The rules about the videos, in one place.
     *
     * They were written twice — once on the landing page and once on the video
     * page of the form — in different words, which is two things to keep true
     * and two things to disagree. Both pages render this list now, so a change
     * on the Wording page changes both.
     *
     * Nothing is uploaded to this app. People put their videos on YouTube or
     * Vimeo and give us a link, which is why there is no longer anything here
     * about file sizes or 4K.
     */
    'video.rule.link': {
        default: 'Upload each video to YouTube or Vimeo as unlisted, then paste the link '
            + 'into the form. Unlisted means anyone with the link can watch it, so do '
            + 'not share it elsewhere. Please keep the videos up until selection has '
            + 'finished.',
        note: 'How the videos reach us. Nothing is uploaded here; we keep a link.',
        guard: {
            mustContain: 'unlisted',
            because: 'Applicants need to know that an unlisted video can be watched by anyone '
                + 'with the link before they choose what to upload.',
        },
    },
    'video.rule.recorded': {
        default: 'Recorded within the last 6 weeks, and reflecting what you are currently '
            + 'studying or performing.',
        note: 'One of the rules about the videos.',
    },
    'video.rule.visible': {
        default: 'You, your instrument, and both hands clearly visible. HD phone recordings '
            + 'are perfectly fine.',
        note: 'One of the rules about the videos.',
    },
    'video.rule.length': {
        default: 'The combined total length of all videos should not exceed 10 minutes.',
        note: 'One of the rules about the videos.',
    },
    'video.rule.rights': {
        default: 'You must either own the video or have permission to submit it. If you are '
            + 'performing a piece composed by someone else, please include the title and '
            + 'composer. If someone else recorded your video, be sure you have their '
            + 'permission.',
        note: 'One of the rules about the videos.',
    },
    'home.apply.lede': {
        default: 'Simply fill in the application form, in which you will be asked several '
            + 'questions about your instrument of choice, location and experience. You '
            + 'will also be asked for a link to each of the following audition videos:',
        note: 'The line introducing the three kinds of video. Links, not uploads: nothing is sent to us.',
    },
    'home.apply.excerpt': {
        default: 'A short prepared excerpt (materials are downloadable from the form).',
        note: 'The first video an application asks for.',
    },
    'home.apply.pieces': {
        default: 'Two short, contrasting pieces.',
        note: 'The second and third videos an application asks for.',
    },
    'home.apply.statement': {
        default: 'A brief video telling us why you would like to be part of this orchestra.',
        note: 'The fourth video an application asks for.',
    },
    'home.createAccount': {
        default: 'Create an account',
        note: 'The coral button on the home page for somebody who has not applied '
            + 'before. It opens the sign-up form directly rather than a sign-in form '
            + 'they would have to find their way out of.',
    },
    'home.signIn': {
        default: 'Sign in',
        note: 'Beside the create-account button, for anybody who already has an '
            + 'account: an applicant coming back to a part-finished application, and '
            + 'also the adjudicators, the conductor and the team, who all arrive '
            + 'here too. Deliberately not "sign in to resume" for that reason.',
    },
    'home.continue': { default: 'Go to my applications', note: 'The one coral button on the home page once somebody is signed in.' },
    'auth.needSignIn': {
        default: 'Please sign in to see this page.',
        note: 'Shown in place of a page that needs an account, with a sign-in button.',
    },
    'auth.signIn': { default: 'Sign in', note: 'The button on the sign-in prompt.' },
    'auth.signingIn': {
        default: 'Signing you in…',
        note: 'Shown during the sign-in callback. Says what is happening rather than spinning.',
    },
    /* ── empty states ─────────────────────────────────────────────────────── */
    'apply.closed': {
        default: 'Applications have closed.',
        note: 'Shown in place of the list when nothing is open to apply for.',
    },
    'apply.closedOn': {
        default: 'Applications for {{name}} closed on {{date}}.',
        note: 'Shown when the most recent audition has been and gone.',
        vars: ['name', 'date'],
    },
    'apply.opensOn': {
        default: 'Applications for {{name}} open on {{date}}.',
        note: 'Shown when the next audition has not opened yet.',
        vars: ['name', 'date'],
    },
    'apply.openAuditions': {
        default: 'Open for auditions',
        note: 'Heading above the list of instruments somebody can apply for.',
    },
    'apply.start': {
        default: 'Apply for {{instrument}}',
        note: 'The link that starts an application for one instrument.',
        vars: ['instrument'],
    },
    'apply.places': {
        default: '{{places}} places',
        note: 'How many places a section has, beside its name. Used when there is more than one.',
        vars: ['places'],
    },
    'apply.placesOne': {
        default: '1 place',
        note: 'The singular of apply.places. Timpani sections usually have exactly one.',
    },
    'apply.continue': {
        default: 'Continue your application for {{instrument}}',
        note: 'Replaces "Apply for" once there is a part-finished application for that '
            + 'instrument, so somebody can tell the two apart from the list.',
        vars: ['instrument'],
    },
    'apply.status.inProgress': {
        default: 'In progress',
        note: 'The status of a started-but-unsent application. A beta tester asked for '
            + 'it: his half-finished form appeared nowhere, so there was no way to '
            + 'tell he had started one.',
    },
    'apply.status.lastSaved': {
        default: 'Last saved {{when}}',
        note: 'Under an in-progress application, so it is clear the answers are kept.',
        vars: ['when'],
    },
    'apply.discard': {
        default: 'Discard this application',
        note: 'Offered while a form is still empty, for somebody who opened the wrong '
            + 'instrument. It deletes the draft rather than leaving one behind.',
    },
    'apply.discarded': {
        default: 'That application has been discarded.',
        note: 'Said on My Applications after a draft is thrown away.',
    },
    'preference.heading': {
        default: 'If you were offered more than one',
        note: 'Over the block on My Applications where somebody who has applied for '
            + 'more than one instrument puts them in order. It only appears when '
            + 'there is more than one.',
    },
    'preference.lede': {
        default: 'You have applied for more than one instrument. If you were offered a '
            + 'place on more than one, which would you rather play? Put them in '
            + 'order, with the one you would most like at the top.',
        note: 'Asks the question. Plain, and in the conditional: it is not an offer.',
    },
    'preference.note': {
        default: 'You can change this until applications close. It helps us ask the right '
            + 'question if it comes up — it is not a promise either way, since which '
            + 'places are free depends on everyone else too.',
        note: 'Says what the answer is for and what it is not. It must keep saying that '
            + 'this does not bind anybody: the team still decides, and somebody who '
            + 'read this as a guarantee would have a fair complaint.',
        guard: {
            mustContain: 'not a promise',
            because: 'An applicant ordering their instruments must not come away believing '
                + 'they have been promised the one at the top.',
        },
    },
    'preference.first': {
        default: 'First choice',
        note: 'Marks the instrument at the top of their order.',
    },
    'preference.position': {
        default: 'Choice {{position}}',
        note: 'Marks each instrument below the first in their order.',
        vars: ['position'],
    },
    'preference.up': {
        default: 'Move {{instrument}} up',
        note: 'The button that moves one instrument up the order. Named, so a screen reader says which.',
        vars: ['instrument'],
    },
    'preference.down': {
        default: 'Move {{instrument}} down',
        note: 'The button that moves one instrument down the order, named for the same '
            + 'reason as the one above it.',
        vars: ['instrument'],
    },
    'preference.saved': {
        default: 'Saved. {{instrument}} is your first choice.',
        note: 'Confirms the order was recorded, naming the one at the top.',
        vars: ['instrument'],
    },
    'multiSeat.preference': {
        default: 'They said they would rather play {{instrument}}.',
        note: 'On the team\u2019s multi-seat flag: what the applicant themselves said, '
            + 'recorded when they applied rather than asked for in the week the '
            + 'offers go out.',
        vars: ['instrument'],
    },
    'multiSeat.noPreference': {
        default: 'They have not said which they would rather play.',
        note: 'The other case, so the absence of an answer is stated rather than left blank.',
    },
    'apply.alreadyApplied': {
        default: 'You have applied for this',
        note: 'Shown instead of the apply link when an application already exists.',
    },
    'apply.status.submitted': {
        default: 'Application received',
        note: 'The status of a submitted application in the list.',
    },
    /* ── revising a sent application, until the close ──────────────────────── */
    /* ── videos the check thinks are broken ──────────────────────────────── */
    'nav.videos': { default: 'Videos', note: 'Team navigation link to the videos the check thinks will not play.' },
    'videos.eyebrow': { default: 'Team', note: 'Small label above the broken-videos heading.' },
    'videos.title': { default: 'Videos that may not play', note: 'Heading of the page listing videos the nightly check could not watch.' },
    'videos.lede': {
        default: 'The nightly check could not watch these. Open each one before anybody '
            + 'is told their video is broken \u2014 a check can be wrong, and being told '
            + 'to fix something that works is worse than silence.',
        note: 'Explains that the list is the checker\u2019s opinion and should be verified '
            + 'by hand before the applicants are emailed.',
    },
    'videos.none': { default: 'Every video plays.', note: 'Shown when no video has failed its check.' },
    'videos.col.applicant': { default: 'Applicant', note: 'Column heading for whose video it is.' },
    'videos.col.video': { default: 'Video', note: 'Column heading for which of the four videos it is.' },
    'videos.col.status': { default: 'What the check said', note: 'Column heading for the reason the check gave.' },
    'videos.col.checked': { default: 'Checked', note: 'Column heading for when the check last ran on it.' },
    'videos.col.told': { default: 'Applicant told', note: 'Column heading for whether the applicant has been emailed about it.' },
    'videos.open': { default: 'Open', note: 'Link that opens the applicant\u2019s video in a new tab to check it by hand.' },
    'videos.told.no': { default: 'Not yet', note: 'The applicant has not been emailed about their broken video.' },
    'videos.told.queued': { default: 'Queued', note: 'An email about the broken video is waiting to be sent.' },
    'videos.told.sent': { default: 'Told', note: 'The applicant has been emailed about their broken video.' },
    'videos.ok': { default: 'playing normally', note: 'Suffix after the count of videos that passed their check.' },
    'videos.status.private': { default: 'Set to private', note: 'The provider refused the video as private.' },
    'videos.status.deleted': { default: 'Not found', note: 'The provider says the video does not exist.' },
    'videos.status.no_embed': {
        default: 'Cannot be embedded',
        note: 'The video exists but the provider will not allow it to play inside the app.',
    },
    /* ── the mail dashboard ───────────────────────────────────────────────── */
    'nav.mail': { default: 'Mail', note: 'Team navigation link to what the app has emailed.' },
    'mail.eyebrow': { default: 'Team', note: 'Small label above the mail heading.' },
    'mail.title': { default: 'What we have sent', note: 'Heading of the page showing every message the app has tried to send.' },
    'mail.lede': {
        default: 'Every message the app has tried to send, and what became of it. Counts '
            + 'only, except for the few who have asked not to be emailed \u2014 '
            + 'those are named, because somebody has to reach them another way.',
        note: 'Explains the mail page, and which part of it names anybody.',
    },
    'mail.sent': { default: 'Sent', note: 'Messages handed to the email provider.' },
    'mail.sending': { default: 'Sending now', note: 'Messages whose send is running at this moment.' },
    'mail.due': { default: 'Due', note: 'Messages queued whose turn has come, waiting for the worker.' },
    'mail.waiting': {
        default: 'Waiting their turn',
        note: 'Messages queued for later. A paced send spreads these over minutes or hours.',
    },
    'mail.failed': {
        default: 'Failed',
        note: 'Messages that will never be sent, because their job gave up or was cancelled.',
    },
    'mail.delivered': { default: 'Confirmed delivered', note: 'Messages the provider confirmed it delivered.' },
    'mail.bad': {
        default: 'Bounced or complained',
        note: 'Messages that bounced, were refused, or were marked as spam by the recipient.',
    },
    'mail.stopped': {
        default: 'Stopped at their request',
        note: 'Messages never sent because the recipient has asked not to be emailed. Not failures.',
    },
    'mail.next': { default: 'Next leaves', note: 'Label before the time the next paced message is due to go.' },
    'mail.col.template': { default: 'Message', note: 'Column heading for which message it is.' },
    'mail.errors': { default: 'What the failures say', note: 'Heading over the list of error messages from failed sends.' },
    'mail.errors.none': { default: 'Nothing has failed.', note: 'Shown in place of the error list when there are no failures.' },
    'mail.empty': {
        default: 'Nothing has been sent yet.',
        note: 'Shown on the mail page when the app has never queued a message.',
    },
    /*
     * The one panel on this page that names people, and the reason it does.
     * Nothing in the system can email somebody who has opted out -- the
     * suppression has no exception, deliberately -- so an applicant who opts
     * out and is then offered a place can only be told by a person.
     */
    /*
     * Sending again what died. The button exists because sixty-three applicants
     * never got a receipt: the template was unapproved, every attempt was used
     * up, and approving it afterwards reached none of them.
     */
    /* ── finding one applicant ─────────────────────────────────────────────── */
    'nav.applicants': {
        default: 'Applicants',
        note: 'Team navigation link to the page for looking one applicant up.',
    },
    'applicants.eyebrow': { default: 'Team', note: 'Small label above the applicant lookup heading.' },
    'applicants.title': {
        default: 'Find an applicant',
        note: 'Heading of the page for looking up one applicant to answer their email.',
    },
    'applicants.lede': {
        default: 'For answering somebody who has written in. Search by name, or paste the '
            + 'sign-in id from the identity provider \u2014 email addresses are held '
            + 'there, not here, so a search by address starts there.',
        note: 'Explains what the lookup is for and why it cannot search by email.',
    },
    'applicants.search': { default: 'Name or sign-in id', note: 'Label on the search box.' },
    'applicants.search.placeholder': {
        default: 'Surname, full name, or an id',
        note: 'Placeholder inside the search box.',
    },
    'applicants.search.go': { default: 'Search', note: 'Button that runs the search.' },
    'applicants.recorded': {
        default: 'Every search is recorded, with what was searched for.',
        note: 'Says plainly that looking somebody up leaves a trace. It reads dates of birth.',
    },
    'applicants.none': {
        default: 'Nobody matching that.',
        note: 'Shown when a search finds no applicant.',
    },
    'applicants.more': {
        default: 'Showing the first twenty-five. Narrow the search to see the rest.',
        note: 'Shown when a search matched more applicants than are displayed.',
    },
    'applicants.dob': { default: 'Date of birth', note: 'Label beside an applicant\u2019s date of birth.' },
    'applicants.submitted': { default: 'Submitted', note: 'Label beside when they sent their application.' },
    'applicants.notSubmitted': {
        default: 'Started, never submitted',
        note: 'Shown in place of a date for an application that was never sent.',
    },
    'applicants.edited': { default: 'Last changed', note: 'Label beside when they last edited it.' },
    'applicants.signInId': { default: 'Sign-in id', note: 'Label beside the identity provider\u2019s id for them.' },
    'applicants.videos': { default: 'Their four videos', note: 'Heading over the state of each video.' },
    'applicants.open': { default: 'Open', note: 'Link that opens one of their videos in a new tab.' },
    'mail.retry': {
        default: 'Send the ones that failed again',
        note: 'Button that re-queues messages whose sends gave up, once the reason is fixed.',
    },
    'mail.retry.none': {
        default: 'Nothing is waiting to be sent again.',
        note: 'Shown in place of the retry button when no message has failed.',
    },
    'mail.retry.ready': {
        default: 'ready to try again',
        note: 'Follows a number, as in "63 ready to try again".',
    },
    'mail.retry.note': {
        default: 'Fix the reason first \u2014 approve the wording on the Wording page \u2014 or '
            + 'they will fail again. Nobody who has already received one is written to '
            + 'twice, and a \u201cyour video will not play\u201d notice is only sent again if '
            + 'the video still will not play.',
        note: 'Explains what to do before pressing the retry button, and what it will not send.',
    },
    'mail.dropped': {
        default: 'No longer true',
        note: 'Messages never sent because what they said stopped being true \u2014 a video that plays after all. Not failures.',
    },
    'mail.retry.check': {
        default: 'Check the videos and show me what will send',
        note: 'First button: re-checks every video with the providers and reports the verdict without sending.',
    },
    'mail.retry.checking': {
        default: 'Asking the providers\u2026',
        note: 'Shown on the check button while the videos are being re-checked.',
    },
    'mail.retry.willSend': {
        default: '{{n}} messages will be sent again.',
        vars: ['n'],
        note: 'How many failed messages the retry would queue.',
    },
    'mail.retry.checked': {
        default: '{{checked}} videos re-checked just now: {{send}} applicants will be told '
            + 'theirs will not play, {{drop}} will not, because their videos play.',
        vars: ['checked', 'send', 'drop'],
        note: 'The verdict after re-checking, before anything is sent.',
    },
    'mail.retry.stands': {
        default: 'still will not play \u2014 will be told',
        note: 'Shown beside an applicant whose video is still failing after a re-check.',
    },
    'mail.retry.plays': {
        default: 'plays now \u2014 will not be told',
        note: 'Shown beside an applicant whose video plays after a re-check, so nothing is sent.',
    },
    'mail.retry.sending': {
        default: 'Queued again, one a second.',
        note: 'Confirmation after the retry button is pressed.',
    },
    'mail.optouts': {
        default: 'Not emailed any more',
        note: 'Heading over the people the system will not write to: they unsubscribed, their address bounced for good, or they reported us as spam.',
    },
    'mail.optouts.lede': {
        default: 'They used the unsubscribe link, or their address bounced for good, or '
            + 'they marked us as spam. Nothing can email them now \u2014 it has no '
            + 'exceptions. If one of them is offered a place, somebody has to tell '
            + 'them another way.',
        note: 'Explains why these applicants are listed by name, and the three ways somebody lands here.',
    },
    'mail.optouts.count': {
        default: 'Opted out',
        note: 'Label on the number of accounts that have asked not to be emailed.',
    },
    'mail.optouts.none': {
        default: 'Nobody has opted out.',
        note: 'Shown in place of the opt-out list when nobody has used the link.',
    },
    'mail.optouts.noapplicants': {
        default: 'None of them has applied to this audition.',
        note: 'Shown when people have opted out but none of them is an applicant here.',
    },
    'mail.optouts.notsubmitted': {
        default: 'started, not submitted',
        note: 'Shown beside an opted-out applicant who never finished their application.',
    },
    'mail.optouts.unknown': {
        default: 'The mail service could not be reached, so this list may be incomplete.',
        note: 'Shown on the opt-out panel when notify did not answer.',
    },
    /* ── the team's intake dashboard ───────────────────────────────────────── */
    'nav.dashboard': {
        default: 'Dashboard',
        note: 'Team navigation link to the intake figures.',
    },
    'dash.eyebrow': {
        default: 'Team',
        note: 'Small label above the dashboard heading.',
    },
    'dash.title': {
        default: 'How the round is going',
        note: 'Heading of the page with the intake figures.',
    },
    'dash.accounts': {
        default: 'Accounts created',
        note: 'Label for the number of people who have signed up, whether or not they '
            + 'went on to apply.',
    },
    'dash.accounts.note': {
        default: 'Everyone who has signed up, including those who have not applied.',
        note: 'Explains that the account figure is larger than the applicant figure on '
            + 'purpose, so the gap does not read as a fault.',
    },
    'dash.accounts.unavailable': {
        default: 'Not available just now',
        note: 'Shown in place of the account figure when the identity service cannot '
            + 'be reached. The rest of the page still works.',
    },
    'dash.applicants': {
        default: 'People who have applied',
        note: 'Label for the number of distinct people with a sent application. '
            + 'Counted once each, however many instruments they applied on.',
    },
    'dash.submitted': {
        default: 'Applications sent',
        note: 'Label for the number of sent applications. Higher than the number of '
            + 'people where somebody applied on more than one instrument.',
    },
    'dash.answered': {
        default: 'Being filled in',
        note: 'Label for part-finished applications with at least one answer in them. '
            + 'Deliberately not "started": a draft is removed once it is sent, so '
            + 'this counts only the unsent ones.',
    },
    'dash.answered.note': {
        default: 'At least one answer saved, and not sent yet. Once somebody sends theirs '
            + 'it stops being counted here and appears under applications sent.',
        note: 'Explains that this and the sent figure do not overlap, so the two are '
            + 'not read as a running total.',
    },
    'dash.opened': {
        default: 'Opened, nothing filled in',
        note: 'Label for applications somebody opened without answering anything. The '
            + 'form saves itself as soon as a page is turned, so this is a weak '
            + 'signal and is kept separate from applications being filled in.',
    },
    'dash.opened.note': {
        default: 'Someone opened an application and moved through it without answering '
            + 'anything yet. Worth reading as curiosity rather than intent.',
        note: 'Explains why this number is reported apart from the one next to it, so '
            + 'the two are not added together.',
    },
    'dash.places': {
        default: 'Places',
        note: 'Column heading for how many places a section is casting.',
    },
    'dash.col.instrument': {
        default: 'Instrument',
        note: 'Column heading for the section name on the dashboard table.',
    },
    'dash.notEntry': {
        default: 'Not applied to directly',
        note: 'Flag against a section nobody applies to, such as Violin 1 and Violin 2, '
            + 'which are filled by placing violinists rather than by application.',
    },
    'dash.empty': {
        default: 'No sections are set up yet, so there is nothing to count.',
        note: 'Shown on the dashboard before the audition has any sections.',
    },
    'apply.revise': {
        default: 'Revise your application for {{instrument}}',
        note: 'Link from the applications list to the page where somebody changes a '
            + 'sent application. Shown until applications close.',
        vars: ['instrument'],
    },
    'revise.eyebrow': {
        default: 'Your application',
        note: 'Small label above the heading on the revise page.',
    },
    'revise.title': {
        default: 'Change your application',
        note: 'Heading of the page where somebody revises an application already sent.',
    },
    'revise.lede': {
        default: 'You can change your answers and replace any of your videos until '
            + 'applications close on {{closes}}. Your place in the list does not change.',
        note: 'Explains the window for revising a sent application, and reassures that '
            + 'revising does not count as applying again or lose their place.',
        vars: ['closes'],
    },
    'revise.closed': {
        default: 'Applications have closed, so this can no longer be changed. If something '
            + 'is wrong, please get in touch.',
        note: 'Shown in place of the form once applications have closed. The help page '
            + 'address is shown underneath it.',
    },
    'revise.videos': {
        default: 'Your videos',
        note: 'Heading of the section listing the four videos, each replaceable.',
    },
    'revise.video.current': {
        default: 'Currently',
        note: 'Label before the link to the video currently on an application.',
    },
    'revise.video.replaceWith': {
        default: 'Replace with a new link',
        note: 'Label on the box where somebody pastes the replacement video link. The '
            + 'old link is kept, so this is a replacement and not a deletion.',
    },
    'revise.video.replace': {
        default: 'Replace this video',
        note: 'Button that saves a replacement video link for one of the four videos.',
    },
    'revise.video.replaced': {
        default: 'Replaced. We will check the new link shortly.',
        note: 'Confirmation after a video is replaced. Mentions the check because the '
            + 'link status is worked out in the background, not immediately.',
    },
    'revise.video.broken': {
        default: 'This link is not working',
        note: 'Shown against a video whose background check failed, on the page where '
            + 'it can be replaced.',
    },
    'revise.details': {
        default: 'Your answers',
        note: 'Heading of the section with the editable written answers.',
    },
    'revise.save': {
        default: 'Save your answers',
        note: 'Button that saves the written answers on the revise page.',
    },
    'revise.saved': {
        default: 'Saved.',
        note: 'Confirmation after the written answers are saved on the revise page.',
    },
    'revise.fixed.note': {
        /*
         * Says what the page does and nothing else.
         *
         * It used to add that these two fields "decide whether an application can
         * stand at all", which an applicant wrote in to say she did not
         * understand. She was right, and the worse part was not the clumsiness:
         * it told applicants what a field does in the selection, which invites
         * people to reason about how they are judged from a line on an edit
         * screen. Applicant-facing copy says what to do, not what anything counts
         * for.
         */
        default: 'Your instrument and date of birth cannot be changed here. If either is '
            + 'wrong, email us.',
        note: 'Says that two fields cannot be edited here, so somebody does not think '
            + 'the page is broken for leaving them out. Say what to do about it and '
            + 'nothing about what those fields are used for.',
    },
    'apply.brokenLink': {
        default: 'One of your videos needs attention',
        note: 'Flag on an application whose video link has stopped working.',
    },
    'empty.myApplications': {
        default: 'Nothing here yet. When you apply, your applications will be listed here.',
        note: 'Applicant with no applications.',
    },
    'empty.judging': {
        default: 'No panels assigned to you yet.',
        note: 'The adjudicator dashboard before the team has assigned them a panel.',
    },
    'empty.review': {
        default: 'No sections have uploaded results yet.',
        note: 'The conductor review page before any adjudicator uploads.',
    },
    'empty.masterSheet': {
        default: 'No audition has been set up yet.',
        note: 'The master sheet before the team has created an audition.',
    },
    /* ── the adjudicator ──────────────────────────────────────────────────── */
    'judge.lede': {
        default: 'These are the instruments you are judging. Everything saves as you work, so you can come back to it.',
        note: 'The opening line on the adjudicator dashboard.',
    },
    'judge.deadline': {
        default: 'Results are due by {{date}}.',
        note: 'The results deadline on the adjudicator dashboard.',
        vars: ['date'],
    },
    'judge.progress': {
        default: '{{done}} of {{total}} placed',
        note: 'How far through a section an adjudicator is. Placed means categorised.',
        vars: ['done', 'total'],
    },
    'judge.places': {
        default: '{{places}} places',
        note: 'How many places a section needs, on the dashboard and above the grid.',
        vars: ['places'],
    },
    'judge.resume': { default: 'Resume', note: 'Button that reopens the grid an adjudicator was last working in.' },
    'judge.open': { default: 'Open grid', note: 'Button that opens a section grid for the first time.' },
    'judge.uploaded': {
        default: 'Uploaded {{date}}. Your results are locked.',
        note: 'Shown on a panel whose results have been uploaded.',
        vars: ['date'],
    },
    'judge.guide.title': { default: 'How this works', note: 'Heading of the short in-app guide on the adjudicator dashboard.' },
    'judge.guide.body': {
        default: 'Watch each applicant\u2019s three videos, write your notes, give them a part and a category, and put the section in order. First choices come first, then reserves, then everyone else. When every applicant is placed, upload your results.',
        note: 'The in-app guide repeating the instructions from the team\u2019s invitation email.',
    },
    /* ── the grid ─────────────────────────────────────────────────────────── */
    'grid.caption': {
        default: 'Applicants for {{section}}, in ranked order.',
        note: 'The table caption, read by screen readers before the grid.',
        vars: ['section'],
    },
    'grid.col.rank': { default: 'Rank', note: 'Grid column heading for the ranking position.' },
    'grid.col.applicant': { default: 'Applicant', note: 'Grid column heading for the name and details.' },
    'grid.col.videos': { default: 'Videos', note: 'Grid column heading for the three video buttons.' },
    'grid.col.summary': { default: 'Summary', note: 'Grid column heading for the AI-assisted summary.' },
    'grid.col.notes': { default: 'Notes', note: 'Grid column heading for the adjudicator\u2019s own notes.' },
    'grid.col.part': { default: 'Part', note: 'Grid column heading for the part this player would take.' },
    'grid.col.score': { default: 'Score', note: 'Grid column heading for the score. Only shown where the section is set up to score.' },
    'grid.col.category': { default: 'Category', note: 'Grid column heading for first choice, reserve or not selected.' },
    'grid.col.section': { default: 'Section', note: 'Grid column heading for the subsection a violinist is placed in.' },
    'grid.category.first_choice': {
        default: 'First choice',
        note: 'The category chip for someone the adjudicator wants in the orchestra.',
    },
    'grid.category.reserve': {
        default: 'Reserve',
        note: 'The category chip for someone who would take a place if one opens up.',
    },
    'grid.category.not_selected': {
        default: 'Not selected',
        note: 'The category chip for an applicant who is not being offered a place.',
    },
    'grid.pick.first_choice': {
        default: 'Yes',
        note: 'The short form on the adjudicator\u2019s list, where three of these and the '
            + 'ranking share one line. It sits beside Reserve and No, which is why it is '
            + 'a plain yes rather than "First".',
    },
    'grid.pick.reserve': { default: 'Reserve', note: 'The short form on the adjudicator\u2019s list.' },
    'grid.pick.not_selected': { default: 'No', note: 'The short form on the adjudicator\u2019s list.' },
    'grid.open': {
        default: 'Open {{name}}',
        note: 'The accessible name of a person in the adjudicator\u2019s list. It opens them in the pane beside it.',
        vars: ['name'],
    },
    'grid.chooseSomebody': {
        default: 'Choose somebody on the left to see their videos and everything they wrote.',
        note: 'The right-hand pane before anybody is chosen.',
    },
    'grid.undecided': { default: 'Not decided yet', note: 'Shown where an applicant has no category.' },
    'grid.shortcuts': {
        default: 'Keyboard: \u2191 \u2193 move between people, shift \u2191 \u2193 reorder them, '
            + '1 2 3 for yes, reserve and no, 7 8 9 0 to play a video.',
        note: 'The keyboard shortcuts in the adjudicator\u2019s list, written down where they '
            + 'can be found. A shortcut nobody is told about is a shortcut nobody uses.',
    },
    'grid.showing': {
        default: 'Showing {{name}}',
        note: 'Said out loud when the arrow keys move to somebody, since nothing else announces it.',
        vars: ['name'],
    },
    'grid.andOthers': {
        default: '{{count}} people',
        note: 'Stands in for a list of names too long to read. Twenty-eight names is the '
            + 'same information as "28 people", in a form nobody reads.',
        vars: ['count'],
    },
    'grid.unanswered': {
        default: '\u2014',
        note: 'Stands in for an answer somebody left blank. Every question is shown either '
            + 'way, so an adjudicator can tell "asked and not answered" from "never asked".',
    },
    'grid.principalPriority': {
        default: 'Principal priority',
        note: 'The label on rank 1. A label, not a decision \u2014 no principal is announced at adjudication.',
    },
    'grid.principalPriority.conflict': {
        default: '{{name}} is rank 1 and said they would rather not be principal. Mark them as principal priority anyway?',
        note: 'The confirmation shown when rank 1 said they would rather not lead. It is still a label, and the team decides later.',
        vars: ['name'],
    },
    'grid.leadership': { default: 'Leadership potential', note: 'The flag an adjudicator can set on anyone.' },
    'grid.preference.would_like': { default: 'Would like to be principal', note: 'What this applicant said about being principal.' },
    'grid.preference.open_to': { default: 'Open to being principal', note: 'What this applicant said about being principal.' },
    'grid.preference.would_rather_not': { default: 'Would rather not be principal', note: 'What this applicant said about being principal.' },
    'grid.reserveNotWilling': {
        default: '{{name}} said they did not want to go on the reserve list, so they cannot be placed as a reserve.',
        note: 'Refusal shown when someone tries to make a reserve of a person who said no. Never a silent reclassification.',
        vars: ['name'],
        guard: {
            mustContain: 'reserve list',
            because: 'This has to name what the applicant actually declined, or it reads as an unexplained refusal.',
        },
    },
    'grid.moveUp': { default: 'Move up', note: 'Keyboard control for ranking. Drag and drop alone is not accessible.' },
    'grid.moveDown': { default: 'Move down', note: 'Keyboard control for ranking.' },
    'grid.setRank': { default: 'Rank for {{name}}', note: 'Accessible name of the box where a rank is typed directly.', vars: ['name'] },
    'grid.moved': {
        default: '{{name}} is now {{rank}} of {{total}} in {{section}}.',
        note: 'Announced in the live region after every reorder, so a keyboard user hears where the row went.',
        vars: ['name', 'rank', 'total', 'section'],
    },
    'grid.moveRefused': {
        default: '{{name}} is already at the end of {{category}}. Change their category to move them further.',
        note: 'Announced when a move would cross a category boundary. Crossing it is a recategorisation, not a move.',
        vars: ['name', 'category'],
    },
    'grid.placesFull': {
        default: 'All {{places}} places are taken. Move somebody out to choose somebody else.',
        note: 'Why the first-choice button is unavailable. Shown once as many people have '
            + 'been chosen as there are places.',
        vars: ['places'],
    },
    'grid.placesWarning': {
        default: 'This section needs {{places}} places and has {{count}} first choices.',
        note: 'A warning, not a block: the team may deliberately over- or under-select.',
        vars: ['places', 'count'],
    },
    'grid.noSummary': { default: 'No summary yet', note: 'Where an applicant has no AI-assisted summary.' },
    'grid.summaryLabel': { default: 'AI-assisted summary', note: 'Marks the summary as machine-written. Their full answers are always one click away.' },
    'grid.openApplicant': {
        default: 'Open {{name}}',
        note: 'Link from a grid row to the full materials view for that applicant.',
        vars: ['name'],
    },
    'grid.locked': {
        default: 'These results are uploaded and locked. Suzie can reopen them if something needs to change.',
        note: 'Shown across the top of a grid whose results have been uploaded.',
    },
    'grid.upload': { default: 'Upload results', note: 'The one primary button on the grid.' },
    'grid.uploadBlocked': {
        default: 'Not ready to upload yet',
        note: 'Heading above the list of what is still missing.',
    },
    'grid.uploadDone': {
        default: 'Results uploaded. Thank you \u2014 Suzie has them now.',
        note: 'Confirmation after a successful upload.',
    },
    'grid.empty': {
        default: 'No applicants in this section yet.',
        note: 'A section grid with nobody in it.',
    },
    /* ── what is stopping the upload ──────────────────────────────────────── */
    'blocker.missing_category': {
        default: 'Needs a category: {{names}}',
        note: 'Upload blocker. Every applicant needs first choice, reserve or not selected.',
        vars: ['names'],
    },
    'blocker.missing_part': {
        default: 'Your first choices need a part: {{names}}',
        note: 'Upload blocker. The part has to be one from the section\u2019s list, and it '
            + 'is asked of the first choices only \u2014 a reserve\u2019s part is settled when '
            + 'they are offered a place, because it depends on who declines.',
        vars: ['names'],
    },
    'blocker.missing_score': {
        default: 'Needs a score: {{names}}',
        note: 'Upload blocker, only in sections set up to score.',
        vars: ['names'],
    },
    'blocker.missing_rank': {
        default: 'Needs a place in the ranking: {{names}}',
        note: 'Upload blocker. Everyone has to have a place in the section ranking.',
        vars: ['names'],
    },
    'blocker.rank_1_not_first_choice': {
        default: 'Rank 1 has to be a first choice.',
        note: 'Upload blocker, one per section. Rank 1 carries the principal priority label.',
    },
    'blocker.missing_subsection': {
        default: 'Needs a section: {{names}}',
        note: 'Upload blocker for violin, where each selected or reserve player is placed in Violin 1 or Violin 2.',
        vars: ['names'],
    },
    'blocker.reserve_not_willing': {
        default: 'Said no to the reserve list and cannot be a reserve: {{names}}',
        note: 'Upload blocker. The refusal is explicit rather than a silent reclassification.',
        vars: ['names'],
    },
    /* ── one applicant, everything ────────────────────────────────────────── */
    'applicant.back': { default: 'Back to the grid', note: 'Link from the materials view back to the section grid.' },
    'applicant.next': { default: 'Next applicant', note: 'Moves through the pool from the materials view.' },
    'applicant.previous': { default: 'Previous applicant', note: 'Moves through the pool from the materials view.' },
    'applicant.position': { default: '{{at}} of {{of}}', note: 'Where in the pool this applicant is.', vars: ['at', 'of'] },
    'applicant.answers': { default: 'Their answers', note: 'Heading above the applicant\u2019s own words.' },
    'applicant.whyJoin': {
        default: 'Why they want to join',
        note: 'Asked in writing on last year\u2019s form and now asked as a video, so only '
            + 'applications from before that carry an answer.',
    },
    'applicant.currentInvolvement': {
        default: 'Currently playing with',
        note: 'Which orchestra they play in now. No longer asked \u2014 the three-way orchestra '
            + 'question replaced it \u2014 so only older applications carry an answer.',
    },
    'applicant.video.excerpt': { default: 'Excerpt', note: 'Label for the prepared excerpt video.' },
    'applicant.video.piece_1': { default: 'Piece 1', note: 'Label for the first own-choice video.' },
    'applicant.video.piece_2': { default: 'Piece 2', note: 'Label for the second own-choice video.' },
    'applicant.video.explanation': {
        default: 'Why they would like to join',
        note: 'Label for the fourth video, the one that is spoken rather than played.',
    },
    'applicant.video.broken': {
        default: 'This video cannot be played at the moment.',
        note: 'Shown where a video link has stopped working. The applicant is emailed separately.',
    },
    /* ── the conductor ────────────────────────────────────────────────────── */
    'review.lede': {
        default: 'Every section, with the places it needs against the first choices the adjudicator made.',
        note: 'The opening line on the ensemble review page.',
    },
    'review.status.waiting': { default: 'Waiting for results', note: 'A section whose adjudicator has not uploaded yet.' },
    'review.status.ready': { default: 'Ready for review', note: 'A section whose results have arrived and are waiting for her.' },
    'review.status.changes': { default: 'Changes requested', note: 'A section she has sent back to its adjudicator.' },
    'review.status.approved': { default: 'Approved', note: 'A section she has accepted.' },
    'review.places': {
        default: '{{count}} of {{places}} places',
        note: 'First choices against the places a section needs, on the review page.',
        vars: ['count', 'places'],
    },
    'review.placesWarning': {
        default: 'This does not match the places the section needs.',
        note: 'A warning, not a block: the team may deliberately over- or under-select.',
    },
    'review.open': { default: 'Review section', note: 'Link from the ensemble page into one section.' },
    'review.signOff': { default: 'Sign off ensemble', note: 'The one primary button on the ensemble review page.' },
    'review.signOff.blocked': {
        default: 'Every section has to be approved first. Still waiting on: {{sections}}.',
        note: 'Why the sign-off button is not available yet.',
        vars: ['sections'],
    },
    'review.signOff.explain': {
        default: 'Signing off tells the team the outcomes are ready. It sends nothing to anyone who auditioned.',
        note: 'Sits beside the sign-off button. Sign-off releases nothing by itself, and saying so prevents a bad surprise.',
        guard: {
            mustContain: 'sends nothing',
            because: 'Sign-off must never read as though it announces results. That is a separate, deliberate action.',
        },
    },
    'review.signedOff': {
        default: 'Signed off on {{date}}. The team can send the outcomes when they are ready.',
        note: 'Shown once she has signed off.',
        vars: ['date'],
    },
    'review.empty': {
        default: 'No results have been uploaded yet.',
        note: 'The ensemble review page before any adjudicator uploads.',
    },
    /* ── one section, side by side ────────────────────────────────────────── */
    'section.yours': { default: 'Your ranking', note: 'Column heading over the conductor\u2019s own version.' },
    'section.theirs': { default: 'The adjudicator\u2019s', note: 'Column heading over the adjudicator\u2019s original, which she never changes.' },
    'section.changed': { default: 'Changed', note: 'Marks a row where her version differs from the adjudicator\u2019s.' },
    'section.note': { default: 'Why are you making this change?', note: 'Label on the note box. A note is required on every change she makes.' },
    'section.note.help': {
        default: 'A short note is recorded with every change, so the reasoning is kept with the ranking.',
        note: 'Help text under the note box.',
    },
    'section.save': { default: 'Save changes', note: 'Applies her pending reordering, with the note.' },
    'section.discard': { default: 'Discard changes', note: 'Throws away pending reordering without saving it.' },
    'section.pending': {
        default: '{{count}} change(s) not saved yet.',
        note: 'How many rows she has moved since the last save.',
        vars: ['count'],
    },
    'section.approve': { default: 'Approve section', note: 'Accepts the ranking, including her changes, and saves a dated snapshot.' },
    'section.approved': { default: 'Approved on {{date}}.', note: 'Shown on a section she has already approved.', vars: ['date'] },
    'section.requestChanges': { default: 'Ask for changes', note: 'Sends the section back to its adjudicator with a comment.' },
    'section.requestChanges.help': {
        default: 'The adjudicator is emailed your comment and their results reopen. The section comes back to you once they upload again.',
        note: 'Explains what asking for changes actually does, before she does it.',
    },
    'section.requestChanges.sent': {
        default: 'Sent back to the adjudicator. It will return here once they upload again.',
        note: 'Confirmation after asking for changes.',
    },
    'section.comment': { default: 'What needs changing?', note: 'Label on the comment box for asking for changes.' },
    'section.locked': {
        default: 'This section is back with its adjudicator, so there is nothing to change here yet.',
        note: 'Shown while a section is out for changes.',
    },
    'section.saved': { default: 'Changes saved.', note: 'Confirmation after saving a reordering.' },
    'section.part': {
        default: 'Part',
        note: 'Label on the part chooser in the conductor\u2019s section view. Only the people who hold a place have one.',
    },
    'section.partUnassigned': {
        default: 'Not decided yet',
        note: 'The empty option on a part chooser. Adjudication only asks the first choices for a part, so the others start here.',
    },
    /* ── the team ─────────────────────────────────────────────────────────── */
    'team.setup.lede': {
        default: 'The shape of the audition: when it runs, which panels there are, and what each section needs.',
        note: 'The opening line on the audition setup page.',
    },
    'team.setup.dates': { default: 'Dates', note: 'Heading over the four dates that shape the audition.' },
    'team.setup.opensAt': { default: 'Applications open', note: 'Label for the date applications open.' },
    'team.setup.closesAt': { default: 'Applications close', note: 'Label for the date applications close.' },
    'team.setup.resultsDueAt': { default: 'Results due from adjudicators', note: 'Label for the adjudication deadline.' },
    'team.setup.acceptanceClosesAt': { default: 'Acceptances close', note: 'Label for the date offers expire.' },
    'team.setup.windows': { default: 'Windows', note: 'Heading over the four timing settings, which can change after the audition opens.' },
    'team.setup.reminderOffsetHours': { default: 'Reminder, hours before a deadline', note: 'How long before a deadline the reminder goes out.' },
    'team.setup.reserveResponseDays': { default: 'Days a reserve has to reply', note: 'How long a reserve has to answer an offer.' },
    'team.setup.principalResponseDays': { default: 'Days a principal has to reply', note: 'How long a principal has to answer an invitation.' },
    'team.setup.retentionMonths': { default: 'Months to keep applicant details', note: 'How long personal data is kept after the audition.' },
    'team.setup.save': { default: 'Save', note: 'Saves the audition dates and the four timing settings.' },
    'team.setup.saved': { default: 'Saved.', note: 'Confirmation shown after the setup page saves.' },
    'team.setup.panels': { default: 'Panels and sections', note: 'Heading over the panels and sections that make up the audition.' },
    'team.setup.frozen': {
        default: 'People have already applied, so panels and sections cannot be restructured. Moving them now would reassign applicants without telling them.',
        note: 'Explains why structural editing is refused once applications exist.',
        guard: {
            mustContain: 'without telling them',
            because: 'The reason matters more than the refusal: it is about the applicants, not about the software.',
        },
    },
    'team.setup.addPanel': { default: 'Add a panel', note: 'Button that creates a new panel in this audition.' },
    'team.setup.addSection': { default: 'Add a section', note: 'Button that creates a new section inside a panel.' },
    'team.setup.panelName': { default: 'Panel name', note: 'Label for a new panel\u2019s name.' },
    'team.setup.sectionName': { default: 'Section name', note: 'Label for a new section\u2019s name.' },
    'team.setup.instrument': { default: 'Instrument', note: 'Label for a section\u2019s instrument.' },
    'team.setup.placesRequired': { default: 'Places', note: 'How many players this section needs to fill.' },
    'team.setup.parts': { default: 'Parts, one per line', note: 'The part list an adjudicator chooses from when assessing.' },
    'team.setup.scoring': { default: 'Adjudicators give a score as well as a part', note: 'The only thing that makes a score field appear. Nothing keys off the word percussion.' },
    'team.setup.assigns': { default: 'Applicants apply here and are assigned to a subsection', note: 'Violin works this way: everyone applies as a violinist and is placed in Violin 1 or Violin 2.' },
    'team.setup.entry': { default: 'People apply to this section directly', note: 'Off for a subsection like Violin 1, which nobody applies to.' },
    'team.setup.instrumentBits': {
        default: 'What this instrument prepares',
        note: 'Heading over the three per-instrument fields on an existing section.',
    },
    'team.setup.preparedExcerpt': {
        default: 'The excerpt to prepare',
        note: 'What this section plays for the first video. Shown on the application.',
    },
    'team.setup.excerptUrl': {
        default: 'Link to the music',
        note: 'Where this section downloads the excerpt. Shown on the application as a link.',
    },
    'team.setup.instrumentNotes': {
        default: 'Notes for this instrument',
        note: 'Anything true only of this instrument \u2014 doubling, which drums, how many. Shown on the application.',
    },
    'team.setup.doublingInstrument': {
        default: 'Doubling instrument',
        note: 'What a part in this section may come with \u2014 piccolo, bass trombone. Not a '
            + 'part of its own: every part is offered with and without it, because which '
            + 'chair doubles is not fixed.',
    },
    'team.setup.doublingQuestion': {
        default: 'Doubling question',
        note: 'Asked of everybody applying to this section \u2014 "Do you also play piccolo?". '
            + 'Leave it empty and nothing is asked.',
    },
    'team.setup.saveSection': { default: 'Save this section', note: 'Saves the per-instrument fields.' },
    'team.adjudicators.lede': {
        default: 'Invite an adjudicator by email. They create an account with that address and their panels are waiting for them.',
        note: 'The opening line on the adjudicator invitations page.',
    },
    'team.adjudicators.email': { default: 'Their email address', note: 'Label for the invited address. It has to be the one they sign up with.' },
    'team.adjudicators.panels': { default: 'Panels they will judge', note: 'Which panels this invitation gives them access to.' },
    'team.adjudicators.invite': { default: 'Create the invitation', note: 'Creates the pending grant and produces the link to paste into an email.' },
    'team.adjudicators.sent': {
        /*
         * The app sends this now. The old wording -- "Invitation ready. Send them
         * this link" -- was true while nothing was sent, and became a quiet lie
         * the moment it was: the team would send a second copy by hand, or wonder
         * whether anything had gone at all.
         */
        default: 'Invitation created for {{email}}. They must sign up with that address '
            + '\u2014 any other one leaves them with an empty dashboard.',
        note: 'Shown after an invitation is created. Whether the email went is said '
            + 'separately, because it can be refused while the invitation still stands.',
        vars: ['email'],
        guard: {
            mustContain: 'sign up with',
            because: 'An adjudicator who signs up with a different address gets an empty dashboard and no explanation.',
        },
    },
    'team.adjudicators.name': {
        default: 'Their name (optional)',
        note: 'Optional name for the invitation email. Without it the email says hello to the address.',
    },
    'team.adjudicators.emailed': {
        default: 'The invitation has been emailed to them.',
        note: 'Shown when the invitation email was accepted by the mail service.',
    },
    'team.adjudicators.notEmailed': {
        default: 'The invitation was not emailed: {{why}}. Send them the link below instead.',
        vars: ['why'],
        note: 'Shown when the invitation email was refused. The grants still stand; the link still works.',
    },
    'team.judging.title': {
        default: 'Who can open each panel',
        note: 'Heading over the list of people the authorization service will admit to each panel.',
    },
    'team.judging.lede': {
        default: 'Not who was invited \u2014 who can actually get in. An invitation only '
            + 'becomes access once somebody signs up with that address.',
        note: 'Explains the difference between an invitation and access.',
    },
    'team.judging.nobody': {
        default: 'nobody yet',
        note: 'Shown beside a panel no one can open.',
    },
    'team.judging.since': {
        default: 'since',
        note: 'Precedes the date a judge gained access to a panel.',
    },
    'team.adjudicators.linkFallback': {
        default: 'If it does not reach them, send this link yourself:',
        note: 'Introduces the invitation link, which is now a fallback rather than the only route.',
    },
    'team.adjudicators.link': { default: 'Their link', note: 'The URL the team pastes into their own email.' },
    'team.colleagues.heading': { default: 'Your own team', note: 'Heading over inviting a team member or the conductor.' },
    'team.colleagues.lede': {
        default: 'Invite somebody to run this audition with you, or the conductor who '
            + 'reviews every section and signs it off. Same as above: they sign up '
            + 'with the address you give, and what they need is waiting for them.',
        note: 'The opening line over the team and conductor invitations.',
    },
    'team.colleagues.role': { default: 'What they will do', note: 'Whether this invitation is for a team member or the conductor.' },
    'team.colleagues.team': {
        default: 'Run the audition with you \u2014 setup, wording, principals, sending the outcomes',
        note: 'The team answer. It is the same access you have, including inviting others.',
    },
    'team.colleagues.conductor': {
        default: 'Review every section and sign the audition off',
        note: 'The conductor answer.',
    },
    'team.colleagues.invite': { default: 'Create the invitation', note: 'Creates the pending grant for a colleague.' },
    'team.colleagues.warning': {
        default: 'A team member can do everything you can, including inviting other people.',
        note: 'Shown under the team answer. It is a real handover of control, so it is said plainly.',
    },
    'team.wording.lede': {
        default: 'Every word the app shows, and every message it sends. Nothing is sent until its wording is approved.',
        note: 'The opening line on the wording page.',
    },
    'team.wording.interface': { default: 'On screen', note: 'Heading over the interface wording the team can edit.' },
    'team.wording.messages': { default: 'Messages', note: 'Heading over the email and text message templates.' },
    'team.wording.default': { default: 'Shipped wording', note: 'Labels the original text beside an edited one.' },
    'team.wording.reset': { default: 'Use the shipped wording', note: 'Removes an override and goes back to the wording the app shipped with.' },
    'team.wording.save': { default: 'Save wording', note: 'Saves an edited version of one interface string.' },
    'team.wording.subject': {
        default: 'Subject',
        note: 'Label over the subject line of an email template.',
    },
    'team.wording.body': {
        default: 'Message',
        note: 'Label over the body of a message template. The {{fields}} in it are '
            + 'filled in per recipient and must be left as they are.',
    },
    'team.wording.saveFirst': {
        default: 'Save your changes before approving.',
        note: 'Shown beside a template with unsaved edits, where approving would '
            + 'otherwise approve the old wording.',
    },
    'team.wording.approve': { default: 'Approve', note: 'Approves a message template so it can be sent.' },
    'team.wording.approved': { default: 'Approved', note: 'Marks a message template the team has already approved.' },
    'team.wording.unapproved': {
        default: 'Not approved yet, so nothing using it will send.',
        note: 'Shown against a template nobody has approved.',
    },
    'team.wording.required': {
        default: 'This wording has to keep the phrase "{{phrase}}". {{because}}',
        note: 'Shown against a guarded string, so the team knows before they edit rather than after.',
        vars: ['phrase', 'because'],
    },
    'team.wording.mergeFields': { default: 'Available: {{fields}}', note: 'Which placeholders a template may use.', vars: ['fields'] },
    /* ── principals and the release ───────────────────────────────────────── */
    'principals.lede': {
        default: 'One principal per section, proposed from the approved ranking and what each player said about the role. Change any of them before the outcomes go out.',
        note: 'The opening line on the principals page.',
    },
    'principals.propose': { default: 'Work out the proposals', note: 'Generates a proposal per section from the rankings.' },
    'principals.approve': { default: 'Approve every proposal', note: 'The team accepts the whole list, which the release then requires.' },
    'principals.approved': { default: 'Approved', note: 'Marks a principal proposal the team has already accepted.' },
    'principals.reason.rank_1': { default: 'Top of the ranking', note: 'Why this person was proposed: they are rank 1 and did not decline the role.' },
    'principals.reason.rank_1_declined_preference': {
        default: 'The higher-ranked player would rather not',
        note: 'Why this person was proposed instead of rank 1.',
    },
    'principals.reason.manual_swap': { default: 'Chosen by hand', note: 'Why this person was proposed: somebody picked them.' },
    'principals.conflict': {
        default: 'This player said they would rather not be principal. Nobody else in the section wants it either, so they are proposed anyway — they can still say no.',
        note: 'Shown against a proposal whose player declined the role in their application.',
        guard: {
            mustContain: 'they can still say no',
            because: 'Proposing somebody who said no needs the reassurance beside it, or it reads as overriding them.',
        },
    },
    'principals.swap': { default: 'Choose somebody else', note: 'Opens the list of that section\u2019s players to pick a different principal.' },
    'principals.swapNote': { default: 'Why the change?', note: 'The note recorded with a principal swap.' },
    'principals.empty': { default: 'No proposals yet.', note: 'The principals page before anything has been worked out.' },
    'release.lede': {
        default: 'One confirmation sends every outcome: places, reserves and the rest. Nothing has been sent yet.',
        note: 'The opening line on the release page.',
        guard: {
            mustContain: 'Nothing has been sent',
            because: 'This page is the last moment before the app speaks to people, and it should say so.',
        },
    },
    'release.counts': {
        default: '{{selected}} places, {{principal}} principals, {{reserve}} reserves, {{unsuccessful}} unsuccessful — {{total}} messages.',
        note: 'What the release will send, above the confirmation.',
        vars: ['selected', 'principal', 'reserve', 'unsuccessful', 'total'],
    },
    'release.blocked': { default: 'Not ready to send', note: 'Heading above the list of what is stopping the release.' },
    'release.send': { default: 'Send {{total}} outcome messages', note: 'The one confirmation button on the release page.', vars: ['total'] },
    'release.confirm': {
        default: 'This sends every message now, and cannot be undone. Send them?',
        note: 'The confirmation before the outcome wave. It is the one action in the app that cannot be taken back.',
        guard: {
            mustContain: 'cannot be undone',
            because: 'The outcome wave reaches everyone who auditioned. Nobody should press it without being told that.',
        },
    },
    'release.sent': {
        default: 'Sent. {{count}} messages are on their way.',
        note: 'Confirmation after the outcome wave.',
        vars: ['count'],
    },
    'release.already': {
        default: 'The outcomes went out on {{date}}. Sending again would send nothing twice.',
        note: 'Shown on the release page once the wave has gone.',
        vars: ['date'],
    },
    'release.blocker.not_signed_off': { default: 'Suzie has not signed the ensemble off yet.', note: 'A release blocker: the conductor has not signed off.' },
    'release.blocker.principals_not_chosen': { default: 'Some sections have no approved principal.', note: 'A release blocker: a section has no approved principal.' },
    'release.blocker.unresolved_multi_seat': { default: 'Somebody holds a place in more than one section.', note: 'A release blocker: somebody holds two places.' },
    'release.blocker.template_unapproved': { default: 'Some message wording has not been approved.', note: 'A release blocker: message wording is unapproved.' },
    'multiSeat.heading': { default: 'One person, two places', note: 'Heading over the people who hold a place in two sections.' },
    'multiSeat.explain': {
        default: '{{name}} is a first choice in more than one section. Ask them which they want, then record it here. The other place becomes a reserve.',
        note: 'Explains a multi-seat flag and what to do about it.',
        vars: ['name'],
    },
    'multiSeat.keep': { default: 'They are keeping', note: 'Label for choosing which section a multi-seat applicant stays in.' },
    'multiSeat.resolve': { default: 'Record their answer', note: 'Records which place a two-place applicant is keeping.' },
    /* ── offers, as the applicant sees them ───────────────────────────────── */
    'offer.heading': { default: 'Your offer', note: 'Heading over an offer on the applicant\u2019s own page.' },
    'offer.place': { default: 'A place in {{section}}', note: 'What a place offer is for.', vars: ['section'] },
    'offer.principal': { default: 'Principal of {{section}}', note: 'What a principal invitation is for.', vars: ['section'] },
    'offer.reserve': { default: 'A place in {{section}}, from the reserve list', note: 'What a reserve offer is for.', vars: ['section'] },
    'offer.respondBy': { default: 'Please reply by {{date}}.', note: 'The deadline on an offer.', vars: ['date'] },
    'offer.accept': { default: 'Accept', note: 'Button that accepts an offer of a place or a role.' },
    'offer.acceptPlaceOnly': {
        default: 'Accept the place, but not the principal role',
        note: 'The middle reply, only on a principal invitation. Turning the role down does not affect their place.',
    },
    'offer.decline': { default: 'Decline', note: 'Turns an offer down, giving up the place.' },
    'offer.declineConfirm': {
        default: 'Declining gives up your place. Are you sure?',
        note: 'Confirmation before declining, because it cannot be taken back.',
    },
    'offer.answered': { default: 'You replied: {{response}}', note: 'Shown on an offer that has been answered.', vars: ['response'] },
    'offer.response.accepted': { default: 'accepted', note: 'How an accepted reply reads back to the applicant.' },
    'offer.response.accepted_place_only': { default: 'accepted the place, not the role', note: 'How the middle reply reads back.' },
    'offer.response.declined': { default: 'declined', note: 'How a declined reply reads back to the applicant.' },
    'offer.response.no_response': { default: 'no reply by the deadline', note: 'How a lapsed offer reads back.' },
    /* ── reserves, as the conductor sees them ─────────────────────────────── */
    'reserves.heading': { default: 'Reserves', note: 'Heading over a section\u2019s reserve list.' },
    'reserves.open': {
        default: '{{count}} place(s) open in this section.',
        note: 'How many places need filling before Suzie picks a reserve.',
        vars: ['count'],
    },
    'reserves.part': {
        default: 'The part this place is for',
        note: 'Which part the reserve is being offered. Which one is free is only known once somebody declines, so it is asked here rather than during adjudication.',
    },
    'reserves.offer': { default: 'Offer this place', note: 'Sends a reserve offer. Suzie does this herself; nothing goes out on its own.' },
    'reserves.waiting': { default: 'Waiting for their reply', note: 'Shown against a reserve who has an offer out.' },
    'reserves.none': { default: 'No reserves in this section.', note: 'Shown when a section has nobody on its reserve list.' },
    /* ── the master sheet ─────────────────────────────────────────────────── */
    'master.lede': {
        default: 'Where every section stands, from applications to confirmed players.',
        note: 'The opening line on the master sheet.',
    },
    'master.col.panel': { default: 'Panel', note: 'Master sheet column: the adjudicator\u2019s group.' },
    'master.col.section': { default: 'Section', note: 'Master sheet column: the section within a panel.' },
    'master.col.applications': { default: 'Applications', note: 'Master sheet column: how many people applied.' },
    'master.col.adjudication': { default: 'Adjudication', note: 'Master sheet column: whether results have been uploaded.' },
    'master.col.review': { default: 'Review', note: 'Master sheet column: where the conductor has got to.' },
    'master.col.places': { default: 'Places', note: 'Master sheet column: how many players the section needs.' },
    'master.col.choices': { default: 'First choices and reserves', note: 'Master sheet column: the counts, expandable to names.' },
    'master.col.confirmed': { default: 'Confirmed', note: 'Master sheet column: how many have accepted their place.' },
    'master.col.fill': { default: 'Fill', note: 'Master sheet column: whether the section is short, full or oversubscribed.' },
    'master.col.principal': { default: 'Principal', note: 'Master sheet column: who is proposed, invited or confirmed.' },
    'master.waiting': { default: 'Waiting', note: 'A section whose adjudicator has not uploaded results yet.' },
    'master.uploaded': { default: 'Uploaded', note: 'A section whose results have arrived.' },
    'master.fill.gap': { default: 'Gap', note: 'A section with fewer confirmed players than places.' },
    'master.fill.full': { default: 'Full', note: 'A section with exactly as many confirmed players as places.' },
    'master.fill.oversubscribed': { default: 'Oversubscribed', note: 'A section with more confirmed players than places.' },
    'master.names': { default: 'Show the names', note: 'Expands a section\u2019s counts into the ranked names.' },
    'master.exports': { default: 'Download a list', note: 'Heading over the four downloadable lists.' },
    'master.export.orchestra': { default: 'The orchestra', note: 'The selected players, by section, principals first.' },
    'master.export.reserves': { default: 'Reserves', note: 'Every section\u2019s reserve list, in offer order.' },
    'master.export.contacts': { default: 'Contacts and confirmations', note: 'Everyone who auditioned, with how they replied.' },
    'master.export.rejections': { default: 'Not selected', note: 'The people who were not offered a place.' },
    'master.export.note': {
        default: 'Every download carries the provisional-rankings line, and records who took it.',
        note: 'Sits beside the downloads. These are lists of people, and who copied them matters.',
    },
    /* ── page titles ──────────────────────────────────────────────────────── */
    'page.myApplications': { default: 'My applications', note: 'Heading on the applicant applications page.' },
    'page.judging': { default: 'Judging', note: 'Heading on the adjudicator dashboard.' },
    'page.review': { default: 'Ensemble review', note: 'Heading on the conductor ensemble review page.' },
    'page.masterSheet': { default: 'Master sheet', note: 'Heading on the team master sheet.' },
    'page.setup': { default: 'Audition setup', note: 'Heading on the team audition setup page.' },
    'page.adjudicators': { default: 'Adjudicators', note: 'Heading on the team adjudicator invitations page.' },
    'page.wording': { default: 'Wording', note: 'Heading on the team page for interface copy and message templates.' },
    'page.principals': { default: 'Principals', note: 'Heading on the team principals page.' },
    'page.release': { default: 'Send the outcomes', note: 'Heading on the team release page.' },
    'page.notFound': { default: 'Not here', note: 'Heading shown when a page does not exist or is not theirs to open.' },
    /* ── not found ────────────────────────────────────────────────────────── */
    'notFound.body': {
        default: 'That page does not exist, or is not something your account can open.',
        note: 'Deliberately covers both cases: whether a thing exists can itself be private.',
    },
    'notFound.back': { default: 'Back to the audition', note: 'The way out of the not-found page, back to the home page.' },
    /* ── errors ───────────────────────────────────────────────────────────── */
    'error.generic': {
        default: 'Something went wrong. Please try again.',
        note: 'Fallback when no specific message arrives.',
    },
    /*
     * A permission granted after somebody signed in is not in the token they
     * are carrying. It happened to the first invited judge, whose role was
     * attached a second after her token was minted, and the app refused her
     * with "Requires scope auditions:review" until somebody worked out why.
     */
    'auth.accessChanged': {
        default: 'Your access has changed since you signed in, and refreshing it did not '
            + 'take. Sign in again to pick it up \u2014 nothing else is wrong and '
            + 'nothing is lost.',
        note: 'Shown only after the automatic refresh failed to clear a permission '
            + 'refusal. The usual case heals itself and nobody sees this.',
    },
    'auth.accessChanged.action': {
        default: 'Sign in again',
        note: 'Button beside the message about access having changed.',
    },
    'error.unauthenticated': { default: 'Please sign in again.', note: 'Shown when the session has expired or the token was rejected.' },
    'error.forbidden': {
        default: 'This is not something your account can open.',
        note: 'The permission check said no.',
    },
    'error.notFound': { default: 'That is not here.', note: 'Shown when the thing asked for does not exist, or is not theirs to see.' },
    'error.conflict': {
        default: 'This changed somewhere else. Reload and try again.',
        note: 'Two tabs, two devices, or a deadline that has since passed.',
    },
    'error.validationFailed': {
        default: 'Some answers need another look.',
        note: 'The form has problems. The specific fields are marked individually.',
    },
    'error.unavailable': {
        default: 'We cannot reach part of the system. Please try again shortly.',
        note: 'A dependency is down. The app fails closed rather than guessing.',
    },
    'error.timeout': {
        default: 'That took too long to answer. Please check your connection and try again.',
        note: 'A request that ran past its deadline, which on a phone usually means the signal went.',
    },
    'error.offline': {
        default: 'We could not reach the server. Please check your connection and try again.',
        note: 'The request never got out at all.',
    },
    'auth.signInFailed': {
        default: 'We could not finish signing you in.',
        note: 'Shown on the sign-in callback when the exchange fails or does not finish in time.',
    },
    'auth.signInRetry': {
        default: 'Try signing in again',
        note: 'The way out of a sign-in that did not finish, so nobody is left on a page that waits.',
    },
    /* ── the application form ─────────────────────────────────────────────── */
    'apply.saving': { default: 'Saving…', note: 'Shown while an autosave is in flight.' },
    'apply.savedAt': {
        default: 'Saved at {{time}}',
        note: 'The autosave indicator on every page of the form.',
        vars: ['time'],
    },
    'apply.loadFailed': {
        default: 'We could not load the answers you had saved, so nothing you type here '
            + 'will be kept yet. Please wait a moment — we are still trying — or '
            + 'reload the page.',
        note: 'Shown when the saved draft could not be read. It matters that this is '
            + 'loud: the form underneath looks empty and inviting, and typing into it '
            + 'used to overwrite the real answers with the blank ones.',
    },
    'apply.saveFailed': {
        default: 'We could not save your answers just now. We will keep trying — do not close this page.',
        note: 'A persistent warning when autosave fails. It retries by itself.',
    },
    'apply.saveAndFinishLater': {
        default: 'Save and finish later',
        note: 'Makes explicit what autosave is already doing, so people feel safe leaving.',
    },
    'apply.next': { default: 'Next', note: 'Moves to the next page of the form.' },
    'apply.back': { default: 'Back', note: 'Moves to the previous page of the form.' },
    'apply.submit': { default: 'Submit application', note: 'The one coral button on the review page.' },
    'apply.stepOf': {
        default: 'Step {{step}} of {{total}}',
        note: 'Progress through the form, above the page title.',
        vars: ['step', 'total'],
    },
    /*
     * What is wrong, in the words of the question it is wrong about.
     *
     * A submission that fails came back as a list of field paths and validator
     * messages — `materials.1.url: Invalid url` — which tells somebody nothing
     * about what to go and change. Each one is named here by the field it
     * belongs to, and the page it is on, so the form can say "go back to Video
     * submissions and give a link for Piece 1".
     */
    'apply.error.heading': {
        default: 'This cannot be sent yet',
        note: 'Over the list of what is wrong, shown after a submission is refused.',
    },
    'apply.error.lede': {
        default: 'Please fix the following and send it again:',
        note: 'Under the heading that says a submission was refused.',
    },
    'apply.error.onPage': {
        default: '{{problem}} \u2014 on {{page}}',
        note: 'One line of the list: what is wrong, and which page of the form it is on.',
        vars: ['problem', 'page'],
    },
    'apply.error.goTo': {
        default: 'Go to {{page}}',
        note: 'The button that takes somebody to the page carrying the first problem.',
        vars: ['page'],
    },
    'apply.cannotContinue': {
        default: 'Please fix the answer marked in red before carrying on.',
        note: 'Shown beside Next when something on the page would make the whole '
            + 'application be refused at the end. Pressing a button that does nothing '
            + 'is the worst of the options.',
    },
    'apply.video.titleNeeded': {
        default: 'Please give the title of this piece.',
        note: 'The two pieces an applicant chooses are named by them; the adjudicator reads it.',
    },
    'apply.video.composerNeeded': {
        default: 'Please give the composer.',
        note: 'The other half of naming a chosen piece.',
    },
    'apply.video.troubleHeading': {
        default: 'Having trouble uploading?',
        note: 'Over the way out for somebody whose links will not work \u2014 a school '
            + 'network, a phone that will not sign in to YouTube, a file that will '
            + 'not go up. It is a question rather than an instruction because most '
            + 'people will not need it.',
    },
    'apply.video.troubleLede': {
        default: 'If you cannot put your videos on YouTube or Vimeo, you can send the '
            + 'files to us directly instead.',
        note: 'What the button below it does, before they press it.',
    },
    'apply.video.troubleHow': {
        default: 'Please send HD files only, and name each of the four files with what it '
            + 'is and your name exactly as you have written it on this application: '
            + '\u201cThe prepared excerpt\u201d, \u201cPiece 1\u201d, '
            + '\u201cPiece 2\u201d and \u201cWhy you would like to join\u201d.',
        note: 'The instructions for the file request. The four names are the names this '
            + 'form gives the four videos, and they have to stay that way: an '
            + 'applicant naming files goes by whichever wording they read last, and a '
            + 'folder of four hundred files is sorted by hand.',
        guard: {
            mustContain: 'name',
            because: 'A folder of unnamed files cannot be matched to applications, and the '
                + 'only person who can name them is the person uploading.',
        },
    },
    'apply.video.troubleButton': {
        default: 'Send the files to us instead',
        note: 'Records that the videos are coming by file request, then opens it.',
    },
    'apply.video.dropboxChosen': {
        default: 'You are sending your videos as files.',
        note: 'Shown in place of the four link boxes once they have chosen that. The '
            + 'links are no longer required, and this says why rather than leaving '
            + 'four boxes that have stopped asking for anything.',
    },
    'apply.video.dropboxAgain': {
        default: 'Open the file request again',
        note: 'For somebody who closed the tab, or has one more file to send.',
    },
    'apply.video.dropboxUndo': {
        default: 'I would rather paste links after all',
        note: 'The way back. Choosing the file request is not a door that locks.',
    },
    'apply.video.dropboxUnavailable': {
        default: 'Sending files is not set up yet. Please use the links above, or email '
            + 'the team if you cannot.',
        note: 'Shown instead of the button when no file request address has been set. '
            + 'Better than a button that leads nowhere.',
    },
    'applicant.videosViaDropbox': {
        default: 'Sent as files, not links',
        note: 'On the adjudicator\u2019s card, where the four videos would be. Without '
            + 'it the card shows four empty slots and no explanation, which reads as '
            + 'an application nobody finished.',
    },
    'applicant.videosViaDropboxWhere': {
        default: 'Open the uploads folder and search for their name.',
        note: 'How an adjudicator actually finds them. Only the team and the panel see this.',
    },
    'team.setup.gateHeading': {
        default: 'The public site',
        note: 'Over the setting that puts a password on the site before it is announced.',
    },
    'team.setup.gateToggle': {
        default: 'Ask for a password before showing the site',
        note: 'The switch itself. The password is set where the site is deployed, not '
            + 'here: this decides whether it is asked for.',
    },
    'team.setup.gateHelp': {
        default: 'For the days between the site going up and the auditions being '
            + 'announced. Anyone signed in already is unaffected, and the password '
            + 'itself is set with the deployment rather than here.',
        note: 'What the switch does and does not do.',
    },
    'team.setup.uploadsHeading': {
        default: 'Sending files instead of links',
        note: 'Over the two Dropbox addresses.',
    },
    'team.setup.fileRequestUrl': {
        default: 'File request address',
        note: 'Where an applicant who cannot use links is sent. They see this one.',
    },
    'team.setup.folderUrl': {
        default: 'Uploads folder address',
        note: 'Where the files arrive. Shown to the team and the adjudicators so they '
            + 'can find an applicant\u2019s videos, and never to an applicant.',
    },
    'team.setup.uploadsHelp': {
        default: 'Leave both empty and the option is not offered. The folder address is '
            + 'shown to adjudicators next to anybody who used it.',
        note: 'What setting these two does.',
    },
    'apply.video.duplicate': {
        default: 'This is the same video as {{other}}. The two pieces should be different.',
        note: 'Shown under a video link that is the same video as one already given. '
            + 'Matching is on the video itself, so a youtu.be link and a youtube.com '
            + 'link to the same recording are caught.',
        vars: ['other'],
    },
    'apply.error.tooYoung': {
        default: 'This cannot be sent, because everyone in the orchestra has to be 18 or '
            + 'over on the day they send their audition videos. Your answers are all '
            + 'still here — please check the year in your date of birth.',
        note: 'Shown on the review page when the date of birth on the form is under 18. '
            + 'Nothing has been sent and nothing has been deleted, and it says so: '
            + 'the message for an application the server has actually refused is '
            + 'legal.under18, which says the opposite.',
    },
    'apply.error.required': {
        default: '{{field}} is needed',
        note: 'A required answer that was left blank.',
        vars: ['field'],
    },
    'apply.error.invalid': {
        default: '{{field}} is not in a form we can read',
        note: 'An answer that was given but cannot be used \u2014 a link that is not a link.',
        vars: ['field'],
    },
    'apply.error.tooLong': {
        default: '{{field}} is longer than we can store',
        note: 'An answer over the length limit.',
        vars: ['field'],
    },
    'apply.error.unknown': {
        default: 'Something is wrong with {{field}}',
        note: 'Where the server reported a problem this form has no better words for.',
        vars: ['field'],
    },
    'apply.required': {
        default: 'Required',
        note: 'Marks a question that has to be answered. Shown beside the label, and '
            + 'announced, so nobody discovers it by being refused.',
    },
    'apply.optional': {
        default: 'Optional',
        note: 'Marks a question that may be left blank, where most of the others may not.',
    },
    'apply.page.personal': { default: 'Personal details', note: 'Title of form page 1.' },
    'apply.page.musical': { default: 'Musical information', note: 'Title of form page 2.' },
    'apply.page.orchestral': {
        default: 'Orchestral information',
        note: 'Title of form page 3. Shown only to somebody who plays, or has played, in an orchestra.',
    },
    'apply.page.videos': { default: 'Video submissions', note: 'Title of the video page.' },
    'apply.page.availability': { default: 'Availability', note: 'Title of the last form page.' },
    'apply.page.review': { default: 'Check your answers', note: 'Title of the review page before submitting.' },
    /* ── personal details ─────────────────────────────────────────────────── */
    'apply.field.firstName': { default: 'First name', note: 'Form label on the personal details page. Required.' },
    'apply.field.lastName': { default: 'Last name', note: 'Form label on the personal details page. Required.' },
    'apply.field.dateOfBirth': { default: 'Date of birth', note: 'Form label. The orchestra is for adults.' },
    'apply.field.dateOfBirthHelp': {
        default: 'You have to be 18 or over on the day you submit your audition videos.',
        note: 'Under the date of birth field. The form refuses an under-18 date and keeps no record of it.',
        guard: {
            mustContain: '18',
            because: 'The form blocks under-18s and keeps no record of the attempt. Saying so ' +
                'at the field saves somebody filling in a form that will be refused.',
        },
    },
    'apply.field.dateOfBirthTooYoung': {
        default: 'You have to be 18 or over on the day you send your audition videos. '
            + 'If that is a typo, please correct the year.',
        note: 'Shown under the date of birth as soon as it reads as under 18, so nobody '
            + 'fills in the whole form before finding out. The mention of a typo is '
            + 'deliberate: a mistyped year is far commoner here than an actual minor.',
    },
    'apply.field.pronouns': { default: 'Pronouns', note: 'Form label on the personal details page. Required.' },
    'apply.field.dietaryNeeds': {
        default: 'Dietary requirements and allergies',
        note: 'Optional, on the personal details page. Asked so the orchestra can look '
            + 'after somebody on the day; it reaches the team and never an adjudicator.',
    },
    'apply.field.dietaryNeedsHelp': {
        default: 'Only if you have any. This is for whoever is feeding everybody, and no part of how you are judged.',
        note: 'Under the dietary question.',
    },
    'apply.field.accessNeeds': {
        default: 'Do you have any access needs or considerations you would like us to be aware of?',
        note: 'Optional. Read by the orchestra team, and not shown to an adjudicator.',
    },
    'apply.field.location': {
        default: 'Your place of residence (city and region)',
        note: 'Form label on the personal details page.',
    },
    'apply.field.country': { default: 'Country', note: 'Form label on the personal details page. Required.' },
    'apply.field.email': { default: 'Email address', note: 'Shown read-only: it comes from the account.' },
    'apply.field.emailHelp': {
        default: 'This comes from the account you signed in with.',
        note: 'Under the read-only email field.',
    },
    'apply.field.phone': { default: 'Mobile number', note: 'Optional, unless they want text messages.' },
    'apply.field.mailingList': {
        default: 'Add me to the mailing list',
        note: 'Optional opt-in on the personal details page.',
    },
    'apply.field.mailingListTerms': {
        default: 'You will receive emails about the orchestra and about Jacob Collier. You ' +
            'can withdraw your consent at any time, and every email says how.',
        note: 'The consent wording beside the mailing list opt-in.',
    },
    'apply.field.smsOptIn': {
        default: 'Send me text messages about my application',
        note: 'Texts say an update is waiting and link to the app; they never carry the outcome.',
    },
    'apply.field.smsTerms': {
        default: 'Up to 20 messages a month. Message and data rates may apply, consent is ' +
            'not a condition of applying, and you can stop them at any time.',
        note: 'The consent wording beside the SMS opt-in.',
    },
    /* ── musical information ──────────────────────────────────────────────── */
    'apply.field.instrument': {
        default: 'Instrument',
        note: 'Shown, not asked: an application is for one instrument, and this is the one it is for.',
    },
    'apply.field.instrumentHelp': {
        default: 'One application per instrument. To audition on another, apply again for that one.',
        note: 'Under the instrument, where last year\u2019s form had a dropdown. The isolation rule, said plainly.',
    },
    'apply.field.professionalOrAmateur': {
        default: 'Do you play professionally or as an amateur?',
        note: 'Asked on the musical information page. The adjudicator sees the answer on the card.',
    },
    'apply.field.professional': { default: 'Professionally', note: 'One of the two answers.' },
    'apply.field.amateur': { default: 'As an amateur', note: 'One of the two answers.' },
    'apply.field.orchestraHistory': {
        default: 'Are you currently playing in an orchestra, or have you previously?',
        note: 'The three-way question that decides whether the orchestral information page is shown.',
    },
    'apply.field.orchestraCurrent': {
        default: 'Yes, I am currently a member of an orchestra',
        note: 'First of the three answers.',
    },
    'apply.field.orchestraPrevious': {
        default: 'Yes, I have previously been a member of an orchestra, but not currently',
        note: 'Second of the three answers. Leads to the same questions as the first.',
    },
    'apply.field.orchestraNever': {
        default: 'No, I have never been part of an orchestra before',
        note: 'Third answer. Skips the orchestral information page.',
    },
    /* ── orchestral information ───────────────────────────────────────────── */
    'apply.field.orchestralExperience': {
        default: 'Please describe your orchestral experience',
        note: 'The long answer the summary is built from. Required of anybody who has played in an orchestra.',
    },
    'apply.field.orchestralExperienceHelp': {
        default: 'Include any notable ensembles, performances or collaborations.',
        note: 'Under the orchestral experience field.',
    },
    'apply.field.principalPreference': {
        default: 'Would you like to be considered for a principal (section leader) position?',
        note: 'Three answers, not two. The adjudicator sees which one beside the '
            + 'ranking, and somebody who would rather not is flagged if they come first.',
    },
    'apply.field.wouldLike': { default: 'I would like to be a principal', note: 'The first of three answers.' },
    'apply.field.openTo': {
        default: 'I am open to being a principal if one is needed',
        note: 'The middle answer, and the reason there are three: willing, without '
            + 'having asked for it.',
    },
    'apply.field.wouldRatherNot': {
        default: 'I would rather not be a principal',
        note: 'The third answer. Flagged if this person is ranked first.',
    },
    'apply.field.leadershipExperience': {
        default: 'Please briefly describe any relevant leadership experience',
        note: 'Shown to anybody who has not ruled a principal position out.',
    },
    /* ── video submissions ────────────────────────────────────────────────── */
    'apply.video.intro': {
        default: 'Four short videos: the prepared excerpt, two contrasting pieces of your '
            + 'own choosing, and one telling us why you would like to be part of this '
            + 'orchestra. Paste a link to each \u2014 nothing is uploaded here.',
        note: 'The opening line of the video page.',
    },
    'apply.video.excerpt': { default: 'The prepared excerpt', note: 'The first of the four videos.' },
    'apply.video.excerptHelp': {
        default: 'Play the excerpt for your instrument. The tempo is a quarter note = 92.',
        note: 'Under the excerpt video field.',
    },
    'apply.field.doublingYes': { default: 'Yes', note: 'One of the two answers to the doubling question.' },
    'apply.field.doublingNo': { default: 'No', note: 'The other answer to the doubling question.' },
    'apply.field.doublingDetail': {
        default: 'Anything you would like to add',
        note: 'Optional, under the doubling question. Where a trombonist says tenor or bass.',
    },
    'applicant.doubling': {
        default: 'Doubling',
        note: 'Heading over the doubling answer on the adjudicator\u2019s card. It is what a part is assigned from.',
    },
    'apply.video.excerptFor': {
        default: 'For {{section}}',
        note: 'Heading over the per-instrument excerpt on the video page.',
        vars: ['section'],
    },
    'apply.video.excerptTempo': {
        default: '\u2669 = 92',
        note: 'The tempo, shown beside the excerpt download on the video page. One '
            + 'value for every instrument: if a section ever needs its own, say so '
            + 'in that section\u2019s instrument notes rather than here. Empty hides it.',
    },
    'apply.video.excerptDownload': {
        default: 'Download the music',
        note: 'The link to this section\u2019s excerpt, when the team has set one.',
    },
    'apply.video.piece1': { default: 'Piece 1', note: 'The second of the four videos.' },
    'apply.video.piece1Help': { default: 'No more than three minutes.', note: 'Under the first piece.' },
    'apply.video.piece2': { default: 'Piece 2', note: 'The third of the four videos.' },
    'apply.video.piece2Help': {
        default: 'Contrasting with piece 1, and no more than three minutes.',
        note: 'Under the second piece.',
    },
    'apply.video.explanation': { default: 'Why you would like to join', note: 'The fourth of the four videos.' },
    'apply.video.explanationHelp': {
        default: 'One or two minutes, in your own words.',
        note: 'Under the explanation video.',
    },
    'apply.video.urlLabel': { default: 'Link to the video', note: 'Label for each of the four link fields.' },
    'apply.video.checking': { default: 'Checking the link…', note: 'While the link is being checked.' },
    'apply.video.ok': { default: 'This video plays.', note: 'When a link checks out.' },
    'apply.field.pieceTitle': { default: 'Title', note: 'Label for a chosen piece title.', vars: [] },
    'apply.field.pieceComposer': { default: 'Composer', note: 'Label for a chosen piece composer.' },
    /* ── availability ─────────────────────────────────────────────────────── */
    'apply.field.reserveList': {
        default: 'If you are not initially selected, would you be willing to be placed on a reserve list?',
        note: 'Answering no means the ranking cannot place them as a reserve.',
    },
    'apply.field.reserveListHelp': {
        default: 'If somebody who is offered a place cannot take it, we may offer that place ' +
            'to a player on the reserve list.',
        note: 'Under the reserve list question.',
    },
    'apply.field.availability': {
        default: 'I am available for the rehearsals and the performance',
        note: 'Checkbox on the availability page. Required. The dates are set per audition.',
    },
    'apply.field.availabilityHelp': {
        default: 'Travel and accommodation are not provided.',
        note: 'Under the availability confirmation.',
    },
    'apply.field.terms': {
        default: 'I have read and agree to the terms and conditions',
        note: 'Checkbox on the availability page. Required.',
    },
    'apply.field.privacy': {
        default: 'I have read the privacy notice',
        note: 'Checkbox on the availability page. Required before submitting.',
    },
    'apply.review.intro': {
        default: 'Please check everything below. You can change your answers until applications close.',
        note: 'The line above the review page.',
    },
    'apply.review.edit': { default: 'Change', note: 'Link from the review page back to a form page.' },
    'apply.review.yes': {
        default: 'Yes',
        note: 'How a ticked box or a yes answer reads back on the review page.',
    },
    'apply.review.no': {
        default: 'No',
        note: 'How an unticked box or a no answer reads back on the review page.',
    },
    'apply.review.name': {
        default: 'Name',
        note: 'Label over the full name on the review page, where the two name fields are shown as one.',
    },
    'apply.submitted.title': { default: 'Application received', note: 'Heading after a successful submit.' },
    'apply.submitted.body': {
        default: 'Thank you. We have sent you a confirmation by email. You can change your ' +
            'answers until applications close.',
        note: 'Shown after submitting, and on the application afterwards.',
    },
    /* ── required wording ─────────────────────────────────────────────────── */
    'legal.provisionalRankings': {
        default: 'These are provisional rankings. Suzie may change seating arrangements ' +
            'and section assignments in rehearsal.',
        note: 'Appears wherever a ranking or seating order is shown. The same sentence ' +
            'is built into the message templates in notify, so an email cannot lose it.',
        guard: {
            mustContain: 'provisional',
            because: 'Part C requires every ranking and seating order to be described as ' +
                'provisional, in the app and in every message and export.',
        },
    },
    /* ── help ─────────────────────────────────────────────────────────────── */
    'help.eyebrow': { default: 'Auditions', note: 'The small label above the help page title.' },
    'help.title': {
        default: 'Help',
        note: 'The heading on the help page, and the word the footer link uses.',
    },
    'help.lede': {
        default: 'If you are having trouble, please email us at',
        note: 'The whole of the help page, bar the address, which follows it as a '
            + 'link. It ends without a full stop on purpose: the address completes '
            + 'the sentence.',
    },
    'help.email': {
        default: 'audience-orchestra@jacobcollier.com',
        note: 'Where to write. The one place this address is kept — the sentence above '
            + 'does not repeat it, so the two cannot disagree. It is also the link.',
        guard: {
            mustContain: '@',
            because: 'It is rendered as a mailto link and is the only way somebody stuck '
                + 'can reach anybody. Something that is not an address leaves them with '
                + 'a dead end on the page that exists for dead ends.',
        },
    },
    'terms.eyebrow': { default: 'Auditions', note: 'The small label above the terms and conditions.' },
    'terms.version': {
        default: 'Version {{version}}',
        note: 'Which terms these are. An application records the version it agreed to, '
            + 'so the version has to be visible on the page it agreed to.',
        vars: ['version'],
    },
    'terms.link': {
        default: 'Read the Terms and Conditions',
        note: 'The link from the application form to the full terms. Opens in a new tab.',
    },
    'legal.under18': {
        default: 'The JC Audience Orchestra is for players aged 18 and over, so we cannot ' +
            'accept this application. Nothing you entered has been kept.',
        note: 'Shown when a date of birth under 18 blocks submission. Says plainly that ' +
            'no record was kept, which is true: the draft is deleted and no ' +
            'application row is written.',
        guard: {
            mustContain: 'kept',
            because: 'No under-18 record is stored, and the person is entitled to be told ' +
                'that rather than left wondering what happened to what they typed.',
        },
    },
};
export const COPY_KEYS = Object.keys(COPY);
export function defaultCopy(key) {
    return COPY[key].default;
}
/** Substitutes {{name}} placeholders. A missing value leaves the text intact
 *  rather than printing "undefined" at somebody. */
export function interpolate(text, vars = {}) {
    return text.replace(/\{\{(\w+)\}\}/g, (whole, name) => name in vars ? String(vars[name]) : whole);
}
/**
 * Checks an override before it is stored. Returns null if it is fine, or the
 * reason it is not.
 */
export function validateOverride(key, value) {
    if (!value.trim()) {
        return 'This cannot be empty. To go back to the shipped wording, reset it instead.';
    }
    if (value.length > 2000)
        return 'This is too long for a piece of interface copy.';
    const entry = COPY[key];
    if (entry.guard && !value.toLowerCase().includes(entry.guard.mustContain.toLowerCase())) {
        return `This wording has to mention "${entry.guard.mustContain}". ${entry.guard.because}`;
    }
    // An override may only use placeholders the string actually supports;
    // anything else would render as literal braces to a person.
    const allowed = new Set(entry.vars ?? []);
    for (const match of value.matchAll(/\{\{(\w+)\}\}/g)) {
        const name = match[1];
        if (name === undefined)
            continue;
        if (!allowed.has(name)) {
            return allowed.size
                ? `{{${name}}} is not available here. This string can use: ${[...allowed].join(', ')}.`
                : `{{${name}}} is not available here. This string takes no placeholders.`;
        }
    }
    return null;
}
/* ── API shapes ───────────────────────────────────────────────────────────── */
export const CopyKeySchema = z.enum(COPY_KEYS);
export const CopyBundle = z.object({
    /** Every key, defaults merged with any overrides. */
    strings: z.record(z.string(), z.string()),
    /** Changes when any override changes, so the client can cache on it. */
    version: z.string(),
});
export const CopyUpdateRequest = z.object({ value: z.string() });
export const CopyAdminEntry = z.object({
    key: CopyKeySchema,
    value: z.string(),
    default: z.string(),
    overridden: z.boolean(),
    note: z.string(),
    vars: z.array(z.string()),
    guard: z.object({ mustContain: z.string(), because: z.string() }).nullable(),
    updatedBy: z.string().nullable(),
    updatedAt: z.string().nullable(),
});
export const CopyAdminList = z.object({ entries: z.array(CopyAdminEntry) });
//# sourceMappingURL=copy.js.map